import React, { useState, useEffect, useCallback } from 'react'
import SafeIcon from '../../common/SafeIcon'
import ImageCard from './ImageCard'
import UploadZone from './UploadZone'
import StorageInfo from '../Storage/StorageInfo'
import ImageGenerationModal from '../SlideEditor/ImageGenerationModal'
import { imageLibraryService } from '../../lib/imageLibraryService'
import { useAuth } from '../../contexts/AuthContext'
import * as FiIcons from 'react-icons/fi'

const { 
  FiX, FiSearch, FiFilter, FiGrid, FiList, FiZap, FiUpload, 
  FiRefreshCw, FiChevronDown, FiImage, FiClock, FiStar, FiCheck 
} = FiIcons

const ImageLibraryModal = ({ 
  isOpen, 
  onClose, 
  onImageSelected,
  projectTitle = '',
  genre = '',
  elementType = 'custom'
}) => {
  const { user } = useAuth()
  
  // State management
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all') // all, recent, uploaded, ai_generated
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [viewMode, setViewMode] = useState('grid') // grid, list
  const [showUploadZone, setShowUploadZone] = useState(false)
  const [showImageGeneration, setShowImageGeneration] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [stats, setStats] = useState(null)

  // Load images when modal opens or filters change
  useEffect(() => {
    if (isOpen && user) {
      loadImages(true) // Reset to first page
      loadStats()
    }
  }, [isOpen, user, selectedFilter, searchTerm, sortBy, sortOrder])

  // Load more images when page changes
  useEffect(() => {
    if (isOpen && user && page > 0) {
      loadImages(false) // Append to existing
    }
  }, [page])

  const loadStats = async () => {
    try {
      const userStats = await imageLibraryService.getUserStats(user.id)
      setStats(userStats)
    } catch (error) {
      console.error('Failed to load stats:', error)
    }
  }

  const loadImages = async (reset = false) => {
    if (loading) return
    
    setLoading(true)
    try {
      const currentPage = reset ? 0 : page
      const limit = 20
      
      const result = await imageLibraryService.getUserImages(user.id, {
        search: searchTerm,
        filter: selectedFilter,
        sortBy,
        sortOrder,
        limit,
        offset: currentPage * limit
      })

      if (reset) {
        setImages(result.images || [])
        setPage(0)
      } else {
        setImages(prev => [...prev, ...(result.images || [])])
      }
      
      setHasMore((result.images || []).length === limit)
    } catch (error) {
      console.error('Failed to load images:', error)
      if (reset) {
        setImages([])
      }
    } finally {
      setLoading(false)
    }
  }

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1)
    }
  }

  const handleUploadSuccess = (uploadedImages) => {
    // Select the first uploaded image if there's only one
    if (uploadedImages.length === 1) {
      setSelectedImage(uploadedImages[0])
    }
    setShowUploadZone(false)
    loadStats() // Refresh stats
    loadImages(true) // Refresh images list to show the new images
  }

  const handleGeneratedImage = (generatedImage) => {
    // Automatically select the generated image so user can immediately use it
    setSelectedImage(generatedImage)
    setShowImageGeneration(false)
    loadStats() // Refresh stats
    loadImages(true) // Refresh images list to show the new image
  }

  const handleDeleteImage = async (imageId) => {
    try {
      console.log('🗑️ Deleting image:', imageId)
      
      // Call the delete function
      await imageLibraryService.deleteImage(imageId, user.id)
      
      // Remove from local state
      setImages(prev => prev.filter(img => img.id !== imageId))
      
      // Clear selection if the deleted image was selected
      if (selectedImage?.id === imageId) {
        setSelectedImage(null)
      }
      
      // Refresh stats
      loadStats()
      
      console.log('✅ Image deleted successfully')
    } catch (error) {
      console.error('❌ Failed to delete image:', error)
      throw error // Re-throw so ImageCard can show the error
    }
  }

  const handleUseSelectedImage = async () => {
    if (selectedImage && onImageSelected) {
      try {
        // Mark image as used
        await imageLibraryService.markAsUsed(selectedImage.id)
        
        // Update local state to reflect the usage
        const updatedImages = images.map(img => 
          img.id === selectedImage.id 
            ? { ...img, last_used_at: new Date().toISOString(), usage_count: (img.usage_count || 0) + 1 }
            : img
        )
        setImages(updatedImages)
        
        // Update selected image state as well
        const updatedSelectedImage = {
          ...selectedImage,
          last_used_at: new Date().toISOString(),
          usage_count: (selectedImage.usage_count || 0) + 1
        }
        setSelectedImage(updatedSelectedImage)
        
        // Refresh stats
        loadStats()
        
        // Call the callback with the selected image
        onImageSelected({
          url: selectedImage.file_url,
          alt: selectedImage.alt_text || selectedImage.file_name,
          width: selectedImage.width,
          height: selectedImage.height
        })
        
        onClose()
      } catch (error) {
        console.error('Error marking image as used:', error)
        // Still proceed with selection even if marking fails
        onImageSelected({
          url: selectedImage.file_url,
          alt: selectedImage.alt_text || selectedImage.file_name,
          width: selectedImage.width,
          height: selectedImage.height
        })
        onClose()
      }
    }
  }

  const getFilteredCount = () => {
    if (!stats) return 0
    switch (selectedFilter) {
      case 'uploaded': return stats.uploaded
      case 'ai_generated': return stats.aiGenerated
      case 'recent': return Math.min(10, stats.total) // Recent shows max 10
      default: return stats.total
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Image Library</h2>
            <p className="text-sm text-gray-600">Select an image or create a new one</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Storage Info */}
            <StorageInfo userId={user?.id} />

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <SafeIcon icon={FiGrid} className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              >
                <SafeIcon icon={FiList} className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          {/* Search and Filters */}
          <div className="flex items-center space-x-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search images..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Quick Filters */}
            <div className="flex items-center space-x-2">
              {['all', 'recent', 'uploaded', 'ai_generated'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {filter === 'all' && 'All'}
                  {filter === 'recent' && 'Recent'}
                  {filter === 'uploaded' && 'Uploaded'}
                  {filter === 'ai_generated' && 'AI Generated'}
                </button>
              ))}
            </div>

            {/* Advanced Filters Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowUploadZone(!showUploadZone)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span className="hidden sm:inline">Upload</span>
            </button>
            
            <button
              onClick={() => setShowImageGeneration(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              <span className="hidden sm:inline">AI Generate</span>
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="p-4 bg-gray-50 border-b">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="created_at">Date Created</option>
                  <option value="file_name">Name</option>
                  <option value="file_size">Size</option>
                  <option value="used_count">Usage</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>

              {/* Results Count */}
              <div className="flex items-end">
                <div className="text-sm text-gray-600">
                  {loading ? 'Loading...' : `${getFilteredCount()} images found`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Zone */}
        {showUploadZone && (
          <div className="p-4 border-b bg-gray-50">
            <UploadZone
              onUploadSuccess={handleUploadSuccess}
              onClose={() => setShowUploadZone(false)}
            />
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Images Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading && images.length === 0 ? (
              <div className="flex items-center justify-center h-48">
                <div className="flex items-center space-x-3 text-gray-500">
                  <SafeIcon icon={FiRefreshCw} className="w-5 h-5 animate-spin" />
                  <span>Loading images...</span>
                </div>
              </div>
            ) : images.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                <SafeIcon icon={FiImage} className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No images found</p>
                <p className="text-sm text-center max-w-md">
                  {searchTerm 
                    ? `No images match "${searchTerm}". Try a different search term.`
                    : 'Start by uploading images or generating them with AI.'
                  }
                </p>
              </div>
            ) : (
              <>
                {/* Images Grid */}
                <div className={`grid gap-4 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6' 
                    : 'grid-cols-1'
                }`}>
                  {images.map((image) => (
                    <ImageCard
                      key={image.id}
                      image={image}
                      isSelected={selectedImage?.id === image.id}
                      onSelect={() => setSelectedImage(image)}
                      onDelete={handleDeleteImage}
                      viewMode={viewMode}
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      {loading ? (
                        <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                      ) : (
                        <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                      )}
                      <span>{loading ? 'Loading...' : 'Load More'}</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Selected Image Section - Bottom Horizontal Layout */}
          {selectedImage && (
            <div className="border-t bg-blue-50 border-blue-200">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-blue-900 flex items-center gap-2">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                    Selected Image
                  </h3>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Clear Selection
                  </button>
                </div>
                <div className="flex items-start gap-4">
                  {/* Image Preview */}
                  <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white rounded-lg overflow-hidden flex-shrink-0 border-2 border-blue-200">
                    <img
                      src={selectedImage.file_url}
                      alt={selectedImage.alt_text || selectedImage.file_name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details Grid */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-blue-900 block">Name:</span>
                      <p className="text-blue-700 text-xs mt-1 break-words">
                        {(selectedImage.file_name || '').length > 20 
                          ? (selectedImage.file_name || '').substring(0, 20) + '...' 
                          : (selectedImage.file_name || 'Untitled')
                        }
                      </p>
                    </div>
                    
                    {selectedImage.width && selectedImage.height && (
                      <div>
                        <span className="font-medium text-blue-900 block">Dimensions:</span>
                        <p className="text-blue-700 text-xs mt-1">{selectedImage.width} × {selectedImage.height}px</p>
                      </div>
                    )}
                    
                    {selectedImage.file_size && (
                      <div>
                        <span className="font-medium text-blue-900 block">Size:</span>
                        <p className="text-blue-700 text-xs mt-1">
                          {selectedImage.file_size ? (selectedImage.file_size / 1024).toFixed(1) + ' KB' : 'Unknown'}
                        </p>
                      </div>
                    )}

                    <div>
                      <span className="font-medium text-blue-900 block">Source:</span>
                      <p className="text-blue-700 capitalize text-xs mt-1">
                        {(selectedImage.source_type || 'upload').replace('_', ' ')}
                      </p>
                    </div>

                    {selectedImage.description && (
                      <div className="sm:col-span-2 lg:col-span-2">
                        <span className="font-medium text-blue-900 block">Description:</span>
                        <p className="text-blue-700 text-xs mt-1 line-clamp-2">
                          {(selectedImage.description || '').length > 80 
                            ? (selectedImage.description || '').substring(0, 80) + '...' 
                            : (selectedImage.description || '')
                          }
                        </p>
                      </div>
                    )}

                    {selectedImage.tags && selectedImage.tags.length > 0 && (
                      <div className="sm:col-span-2 lg:col-span-2">
                        <span className="font-medium text-blue-900 block">Tags:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedImage.tags.slice(0, 3).map((tag, index) => (
                            <span 
                              key={index}
                              className="px-2 py-1 bg-blue-200 text-blue-800 text-xs rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {selectedImage.tags.length > 3 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded">
                              +{selectedImage.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Use Button */}
                  <div className="flex-shrink-0">
                    <button
                      onClick={handleUseSelectedImage}
                      className="flex items-center justify-center space-x-2 px-4 py-3 sm:px-6 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span className="hidden sm:inline">Use This Image</span>
                      <span className="sm:hidden">Use</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>

        {/* Mobile Floating Action Button - Only show when no selected image section is visible */}
        {selectedImage && (
          <div className="sm:hidden fixed bottom-4 right-4 z-10">
            <button
              onClick={handleUseSelectedImage}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiCheck} className="w-5 h-5" />
              <span className="font-medium">Use</span>
            </button>
          </div>
        )}
      </div>

      {/* Image Generation Modal */}
      <ImageGenerationModal
        isOpen={showImageGeneration}
        onClose={() => setShowImageGeneration(false)}
        onImageGenerated={handleGeneratedImage}
        projectTitle={projectTitle}
        genre={genre}
        elementType={elementType}
      />
    </div>
  )
}

export default ImageLibraryModal